import { useEffect, useState } from 'react';
import { t } from 'i18next';

import { custonSooner } from '../../../components/CustopmSooner';
import { isGeocodeNotFound, useUserAddress } from './getUserLocation';

/**
 * Сколько ждём обратное геокодирование, прежде чем предложить ввести адрес
 * руками. Дольше — на плохой сети экран выглядит зависшим.
 */
export const SLOW_GEOCODE_MS = 5000;

/**
 * Название адреса для сохранения: по координатам точки на карте, а если
 * геокодер молчит или отказал — то, что ввёл пользователь.
 *
 * Раньше при незавершённом геокодировании адрес уходил на сервер с одними
 * координатами в `title`, и в списке адресов появлялось «41.29950, 69.24010».
 * Теперь без названия сохранить нельзя: `canSave` держит кнопку выключенной,
 * пока геокодер думает, а при сбое или долгом ожидании `needsFallback`
 * показывает повтор запроса и ручной ввод.
 */
export const useAddressTitle = ([lat, lng]: [number, number]) => {
  const { address, isLoading, isError, error, refetch } = useUserAddress({
    lat,
    lng,
  });
  const [manualTitle, setManualTitle] = useState('');
  const [isSlow, setIsSlow] = useState(false);

  // Ручной ввод относится к точке: переставили булавку — старый текст уже
  // не про этот адрес.
  useEffect(() => {
    setManualTitle('');
  }, [lat, lng]);

  useEffect(() => {
    if (!isLoading) {
      setIsSlow(false);
      return;
    }

    const timer = window.setTimeout(() => setIsSlow(true), SLOW_GEOCODE_MS);

    return () => window.clearTimeout(timer);
  }, [isLoading]);

  // Текст ошибки — наш, а не сырой `Unable to geocode` от Nominatim: «не
  // нашли» и «не смогли спросить» — разные советы пользователю.
  const errorMessage = isError
    ? t(isGeocodeNotFound(error) ? 'address-not-found' : 'address-lookup-failed')
    : '';

  // Всплывашка в стиле приложения — строка адреса под ней остаётся, но её
  // легко не заметить за картой.
  useEffect(() => {
    if (errorMessage) custonSooner.error(errorMessage);
  }, [errorMessage, error]);

  // Ответ геокодера точнее ручного: как только он пришёл, берём его.
  const title = address || manualTitle.trim();

  return {
    address,
    isLoading,
    isError,
    errorMessage,
    needsFallback: !address && (isError || isSlow),
    manualTitle,
    setManualTitle,
    retry: refetch,
    title,
    canSave: title.length > 0,
  };
};
